import React, { useState, useEffect } from 'react'
import '../../asserts/css/custom.css'
import jwt_decode from 'jwt-decode'
import { Col, Row, Button, Form, Input, message, Alert, Card } from 'antd'
import { NumberOutlined, KeyOutlined } from '@ant-design/icons'
import minijust_logo from '../../assets/img/minijust_logo.png'
import { useNavigate } from 'react-router-dom'
import InstitutionUserService from '../../services/InstitutionUserService'
import CryptoJS from 'crypto-js'
import moment from 'moment'

const OTP: React.FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [countdown, setCountdown] = useState(300000)
  const [otpDigits, setOtpDigits] = useState<string[]>(['', '', '', '', '', ''])
  const [successMsg, setSuccessMsg] = useState('')
  const [isAutoVerifying, setIsAutoVerifying] = useState(false)

  const onFinishLogin = (values: any): void => {
    setLoading(true)
    InstitutionUserService.InstitutionUserLogin(values)
      .then((res: any) => {
        const encryptedPhone = CryptoJS.AES.encrypt(res.phone_number, 'secret-key').toString()
        localStorage.setItem('email', values.email)
        localStorage.setItem('phone_number', encryptedPhone)

        // setTimeout(() => {
        //   void message.success(res.message)
        //   if (
        //     localStorage.getItem('email') !== null &&
        //     localStorage.getItem('phone_number') !== null
        //   ) {
        //     navigate('/verify')
        //   }
        //   setLoading(false)
        // }, 1000)
      })
      .catch((err: any) => {
        void message.error(err?.response?.data?.message)
        setErrorMsg(err?.response?.data?.message)
        setLoading(false)
      })
  }

  // Handle Resend OTP when button is clicked
  const handleResendOTP = (): void => {
    // Trigger the OTP verification process again
    onFinishLogin({ otp: '' }) // You may need to adjust this based on how your OTP verification logic works.
    // Optionally reset the countdown
    setCountdown(300000) // Reset countdown to 5 minutes
    void message.success('OTP has been resent!')
    setErrorMsg('')
    setSuccessMsg('A new OTP has been sent to your phone and email.')
  }

  useEffect(() => {
    const e_mail = localStorage.getItem('email')
    const tel = localStorage.getItem('phone_number')

    if (e_mail !== null && tel !== null) {
      setEmail(e_mail)
      const decryptedPhone = CryptoJS.AES.decrypt(tel, 'secret-key').toString(CryptoJS.enc.Utf8)
      setPhone(decryptedPhone)
    }

    return () => {
      localStorage.removeItem('email')
      localStorage.removeItem('phone_number')
    }
  }, [])

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prevCountdown) => {
        if (prevCountdown === 0) {
          clearInterval(timer)
          return 0
        }
        return prevCountdown - 1000
      })
    }, 1000)

    return () => {
      clearInterval(timer)
    }
  }, [])

  // Auto-verify OTP when all 6 digits are entered
  useEffect(() => {
    const otpString = otpDigits.join('')
    // Check if all 6 digits are filled and not currently verifying
    if (otpString.length === 6 && !loading && !isAutoVerifying && email !== '') {
      setIsAutoVerifying(true)
      // Small delay to ensure state is updated
      setTimeout(() => {
        onVerifyOTP({ otp: otpString })
      }, 100)
    }
  }, [otpDigits, loading, isAutoVerifying, email])

  const onVerifyOTP = (values: any): void => {
    setLoading(true)
    setIsAutoVerifying(true)
    const joined = otpDigits.join('')
    const otp = joined.length > 0 ? joined : (values.otp ?? '')
    InstitutionUserService.VerifyOTP({ email, otp })
      .then((res: any) => {
        const decoded: any = jwt_decode(res?.token)
        decoded.token = res?.token
        localStorage.setItem('userInfo', JSON.stringify(decoded))
        switch (decoded?.role) {
          case 'SYSTEM ADMIN':
            navigate('/dashboard')
            break
          case 'MINIJUST FINANCE':
            navigate('/finance-dashboard')
            break
          case 'SYSTEM SUPPORT':
            navigate('/support-dashboard')
            break
          case 'SUPER ADMIN':
            navigate('/super-admin-dashboard')
            break
          default:
            navigate('/institution-dashboard')
        }
        window.location.reload()
        setLoading(false)
        setIsAutoVerifying(false)
      })
      .catch((err: any) => {
        setErrorMsg(err?.response?.data?.message)
        void message.error(err?.response?.data?.message)
        setLoading(false)
        setIsAutoVerifying(false)
        // Reset OTP digits on error to allow retry
        setOtpDigits(['', '', '', '', '', ''])
        const firstInput = document.getElementById('otp-input-0') as HTMLInputElement | null
        if (firstInput !== null) {
          firstInput.focus()
        }
      })
  }

  // OTP inputs helpers
  const handleOtpChange = (index: number, value: string): void => {
    if (!/^[0-9]?$/.test(value)) { return }
    const next = [...otpDigits]
    next[index] = value
    setOtpDigits(next)
    const nextInput = document.getElementById(`otp-input-${index + 1}`) as HTMLInputElement | null
    if (value !== '' && nextInput !== null) { nextInput.focus() }
  }

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Backspace' && otpDigits[index] === '') {
      const prevInput = document.getElementById(`otp-input-${index - 1}`) as HTMLInputElement | null
      if (prevInput !== null) { prevInput.focus() }
    }
  }

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
    e.preventDefault()
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (text.length === 0) { return }
    const filled = Array.from({ length: 6 }, (_, i) => text[i] ?? '')
    setOtpDigits(filled)
    const lastIndex = Math.min(text.length, 6) - 1
    const nextInput = document.getElementById(`otp-input-${lastIndex}`) as HTMLInputElement | null
    if (nextInput !== null) { nextInput.focus() }
  }

  return (
    <div className="w-full">
      <div className="navbar bg-blue-800 text-white">
        <div className="flex items-center p-4 flex-col sm:flex-row gap-2 sm:gap-0 w-full">
          <div className="nav-logo mr-4 flex justify-center sm:justify-start w-full sm:w-auto">
            <img src={minijust_logo} alt="Logo" className="h-12 mx-auto sm:mx-0" />
          </div>
          <div className="nav-content text-standard md:text-title font-semibold w-full sm:w-auto text-center sm:text-left">
            <div className="nav-content-header text-title font-extrabold">MINIJUST - IECMS</div>
            <div className="text-large">Bid Security Management Information System</div>
          </div>
        </div>
      </div>

      <div
        className="login-content bg-gray-50 p-2 sm:p-6 d-flex align-center justify-center"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 0
        }}

      >
        <Row justify="center" style={{ height: '80%' }}>
        <Col xs={24} sm={22} md={12} lg={10}>
            <Card
              bordered={false}
              style={{ width: 1200, maxWidth: '100%', margin: '0 auto', borderRadius: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.08)' }}
              bodyStyle={{ padding: 24 }}
            >
              <div className="text-center mb-4" style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 12 }}>
                <div style={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 'inset 0 0 0 1px #dbeafe'
                }}>
                  <KeyOutlined style={{ color: '#1d4ed8', fontSize: 24 }} />
                </div>
                <div className="login-title text-center text-title sm:text-title font-semibold">
                  <p>Enter the verification code</p>
                </div>
              </div>

              {(errorMsg.length > 0) && (
                <Alert type="error" showIcon message={errorMsg} style={{ marginBottom: 12 }} />
              )}
              {(successMsg.length > 0) && (
                <Alert type="success" showIcon message={successMsg} style={{ marginBottom: 12 }} />
              )}

              <Form
                name="normal_login"
                className="otp-form form-pad"
                initialValues={{ remember: true }}
                onFinish={onVerifyOTP}
                autoComplete="off"
              >
              <Form.Item
                name="otp"
                rules={[{ required: otpDigits.join('').length !== 6, message: 'Please input your 6-digit OTP' }]}
              >
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                  {otpDigits.map((digit, idx) => (
                    <Input
                      key={idx}
                      id={`otp-input-${idx}`}
                      value={digit}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      onChange={(e) => { handleOtpChange(idx, e.target.value) }}
                      onKeyDown={(e) => { handleOtpKeyDown(idx, e) }}
                      onPaste={(e) => { handleOtpPaste(e) }}
                      style={{
                        width: 44,
                        height: 48,
                        textAlign: 'center',
                        fontSize: 18,
                        borderRadius: 8,
                        borderColor: '#e5e7eb'
                      }}
                    />
                  ))}
                </div>
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  disabled={loading}
                  htmlType="submit"
                  loading={loading}
                  className="w-full rounded-md bg-blue-500 hover:bg-blue-600 text-white transition-all duration-200"
                >
                  Verify
                </Button>
              </Form.Item>
              <p className="text-standard text-gray-600 text-center">
                We sent a verification code to your phone ending with <b>.........{phone.slice(-2)}</b>.
              </p>
              <br />
              <p className="text-standard text-gray-600 text-center">
                Code expires in
                <code className="font-semibold ml-2 text-blue-600">
                  {moment(countdown).format('mm:ss')}
                </code>
              </p>
              <div className="flex justify-center">
                <Button
                  type="link"
                  disabled={countdown > 0}
                  className="font-semibold rounded-md transition-all duration-300 px-4 py-2 mt-1"
                  onClick={handleResendOTP}
                >
                  Resend code
                </Button>
              </div>
            </Form>
            </Card>
          </Col>
        </Row>

        <Row className="p-3 fixed bottom-0 left-0 right-0 h-16 bg-gray-800 text-white text-standardall md:text-base">
          <Col xs={24} md={14} className="flex flex-wrap space-x-2 md:space-x-4 justify-center">
        <span
          onClick={() => {
            navigate('/')
          }}
          className="hover:text-blue-500 cursor-pointer"
        >
          Home
        </span>
            <span className="hover:text-blue-500 cursor-pointer">About the System</span>
            <span className="hover:text-blue-500 cursor-pointer">FAQ</span>
            <span className="hover:text-blue-500 cursor-pointer">Support</span>
            <span className="hover:text-blue-500 cursor-pointer">Terms and Conditions</span>
            {/* <span className="hover:text-blue-500 cursor-pointer">Privacy and Policy</span> */}
          </Col>
          <Col xs={24} md={10} className="text-right text-gray-400 mt-2 md:mt-0">
            Copyright@2023 - {new Date().getFullYear()} MINIJUST All rights reserved.
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default OTP
